import { useState, useEffect, useCallback } from 'react';
import { X, User as UserIcon, CreditCard, LogOut, ChevronDown, Crown, Tag, FileText, Download, Loader2, Zap, RefreshCw, AlertTriangle, Trash2, Users, Copy, Check } from 'lucide-react';
import { Button } from '../../ui/button';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { TabHeader } from '../../common/TabHeader';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { QuotaUsageBar } from '../quota/QuotaUsageBar';
import { useQuota } from '../../../contexts/QuotaContext';
import { TopupModal } from '../../common/TopupModal';
import { UpgradePlanModal } from '../../common/UpgradePlanModal';
import { billingService, type Invoice, type ReferralRewardItem } from '../../../services/billingService';
import { apiClient } from '../../../services/apiClient';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { getAccountTypeLabel } from '../../../utils/accountRoleUtils';
import { formatDateForDisplay } from '../../../utils/dateUtils';

interface AccountTabProps {
  onClose: () => void;
}

/**
 * Onglet "Mon compte" — infos utilisateur, quota AI, facturation, déconnexion.
 * Intègre les sections Profil, Consommation AI, Abonnement, Code promo, Factures, Déconnexion.
 */
export function AccountTab({ onClose }: AccountTabProps) {
  const { t } = useLanguage();
  const { user, agency, logout, isGuest } = useAuth();
  const { quota, isLoading: loading, creditsUsed, creditsLimit, purchasedCreditsBalance, canPurchaseTopup, refreshQuota } = useQuota();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['profile', 'quota']));
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Billing state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Referral state
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [maxReferrals, setMaxReferrals] = useState(10);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Referral rewards state
  const [referralRewards, setReferralRewards] = useState<ReferralRewardItem[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [applyingReward, setApplyingReward] = useState<string | null>(null);
  const [copiedRewardCode, setCopiedRewardCode] = useState<string | null>(null);

  // Subscription management state
  const [switchingInterval, setSwitchingInterval] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Date de fin d'accès retournée par cancelSubscription — affichée sans
  // attendre un refresh du user (qui recharge subscriptionStatus/EndsAt).
  const [cancelledEndsAt, setCancelledEndsAt] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const planName = quota?.plan_name ?? '';
  const isFreePlan = planName === 'free' || !planName;
  const currentInterval = user?.billingInterval ?? 'monthly';
  // Priorité à la date locale (juste après annulation), fallback sur le user.
  const effectiveSubscriptionEndsAt =
    cancelledEndsAt ??
    (user?.subscriptionStatus === 'cancelling' ? user?.subscriptionEndsAt ?? null : null);

  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const data = await billingService.getInvoices();
      setInvoices(data);
    } catch (error) {
      Logger.error('Failed to fetch invoices', error);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  useEffect(() => {
    if (isGuest) return;
    fetchInvoices();
    refreshQuota();
  }, [fetchInvoices, refreshQuota, isGuest]);

  // Fetch referral stats
  useEffect(() => {
    if (isGuest) return;
    const fetchReferral = async () => {
      setLoadingReferral(true);
      try {
        const res = await apiClient.get<{ referral_code: string | null; referral_count: number; max_referrals: number }>('/referral/stats');
        setReferralCode(res.data.referral_code);
        setReferralCount(res.data.referral_count);
        setMaxReferrals(res.data.max_referrals);
      } catch (error) {
        Logger.error('Failed to fetch referral stats', error);
      } finally {
        setLoadingReferral(false);
      }
    };
    fetchReferral();
  }, []);

  // Fetch referral rewards (for non-free users)
  useEffect(() => {
    if (isGuest || isFreePlan) return;
    const fetchRewards = async () => {
      setLoadingRewards(true);
      try {
        const rewards = await billingService.getMyReferralRewards();
        setReferralRewards(rewards);
      } catch (error) {
        Logger.error('Failed to fetch referral rewards', error);
      } finally {
        setLoadingRewards(false);
      }
    };
    fetchRewards();
  }, [isFreePlan]);

  const handleCopyRewardCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedRewardCode(code);
    toastService.success(t.billing.referralCopied);
    setTimeout(() => setCopiedRewardCode(null), 2000);
  };

  const handleApplyRewardToSubscription = async (code: string) => {
    setApplyingReward(code);
    try {
      await billingService.applyRewardToSubscription(code);
      toastService.success(t.billing.rewardApplied ?? 'Récompense appliquée à votre abonnement !');
      // Refresh rewards list
      const rewards = await billingService.getMyReferralRewards();
      setReferralRewards(rewards);
    } catch {
      toastService.error(t.billing.rewardApplyError ?? 'Erreur lors de l\'application de la récompense');
    } finally {
      setApplyingReward(null);
    }
  };

  const handleDownloadPdf = async (invoice: Invoice) => {
    try {
      const pdfUrl = await billingService.getInvoicePdfUrl(invoice.id);
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toastService.error(t.billing.toasts.pdfError);
    }
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setApplyingCoupon(true);
    try {
      const result = await billingService.applyCoupon(code);
      if (result.success) {
        toastService.success(t.billing.toasts.couponApplied);
        setCouponCode('');
      } else {
        toastService.error(result.message || t.billing.toasts.couponError);
      }
    } catch {
      toastService.error(t.billing.toasts.couponError);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleSwitchInterval = async () => {
    const targetInterval = currentInterval === 'annual' ? 'monthly' : 'annual';
    setSwitchingInterval(true);
    try {
      const result = await billingService.switchInterval(targetInterval);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        // Switch appliqué sans redirection (aucun paiement requis) — feedback explicite.
        toastService.success(
          t.billing.toasts?.switchIntervalUpdated ?? 'Your subscription has been updated.'
        );
      }
    } catch {
      toastService.error(
        t.billing.toasts?.switchIntervalError ?? 'Unable to update your subscription. Please try again later.'
      );
    } finally {
      setSwitchingInterval(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const result = await billingService.cancelSubscription();
      toastService.success(t.billing.subscriptionCancelled);
      setShowCancelDialog(false);
      if (result.subscription_ends_at) {
        Logger.info(`Subscription ends at ${result.subscription_ends_at}`);
        // Affiche la date de fin d'accès immédiatement, sans attendre un reload du user.
        setCancelledEndsAt(result.subscription_ends_at);
      }
    } catch {
      toastService.error(
        t.billing.toasts?.cancelError ?? 'Unable to cancel your subscription. Please try again later.'
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) return;
    setDeleting(true);
    try {
      await billingService.deactivateAccount(deletePassword);
      toastService.success(t.billing.deleteAccountSuccess);
      logout();
    } catch (err: unknown) {
      // V4-FRONT-ANY-USAGE batch 5 2026-05-29
      // On garde la branche 401 spécifique (mauvais mot de passe) qui doit
      // afficher un message dédié, sinon on délègue à extractApiErrorMessage.
      const status = (err as { response?: { status?: number } } | null | undefined)?.response?.status;
      if (status === 401) {
        toastService.error(t.billing.deleteAccountPasswordError);
      } else {
        toastService.error(
          extractApiErrorMessage(err, t.billing.toasts?.couponError ?? 'An error occurred')
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateReferralCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await apiClient.post<{ referral_code: string }>('/referral/generate-code');
      setReferralCode(res.data.referral_code);
    } catch {
      toastService.error(
        t.billing.toasts?.referralCodeError ?? 'Unable to generate your referral code. Please try again later.'
      );
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode);
    setCodeCopied(true);
    toastService.success(t.billing.referralCopied);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const formatAmount = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(amount);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const isSectionExpanded = (section: string) => expandedSections.has(section);

  return (
    <div className="h-full flex flex-col">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader title={t.quota.accountTitle} onClose={onClose} />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">

          {/* Section: Profil */}
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid var(--color-border)` }}>
            <button
              onClick={() => toggleSection('profile')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{
                backgroundColor: isSectionExpanded('profile') ? 'var(--color-surface)' : 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSectionExpanded('profile') ? 'var(--color-surface)' : 'transparent'}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <UserIcon className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.sidebar.account}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('profile') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('profile') && (
              <div className="p-4" style={{ borderTop: `1px solid var(--color-border)`, backgroundColor: 'var(--color-surface)' }}>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
                  >
                    {user?.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.email} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <UserIcon size={22} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                      {user?.email ?? '—'}
                    </p>
                    {/* V4-ACCOUNT-01 2026-05-29 — map raw role to i18n label */}
                    {/* V4-ACCOUNT-ROLE-AGENCY-LABEL 2026-05-29 — pour un compte
                        AGENCY, user.role vaut toujours "user" (rôle de base) :
                        le vrai rôle est porté par agency.role (agency_owner /
                        agency_member). On le priorise quand l'agence existe. */}
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {getAccountTypeLabel(agency?.role ?? user?.role, t)}
                    </p>
                  </div>
                </div>

                {/* Abonnement */}
                {quota?.plan_display_name && (
                  <div
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: 'var(--color-background, var(--color-surface))' }}
                  >
                    <Crown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      {t.quota.subscription}
                    </span>
                    <span className="text-xs font-semibold ml-auto" style={{ color: 'var(--color-primary)' }}>
                      {quota.plan_display_name}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section: Consommation AI — hidden for guests */}
          {!isGuest && <div className="rounded-xl overflow-hidden" style={{ border: `1px solid var(--color-border)` }}>
            <button
              onClick={() => toggleSection('quota')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{
                backgroundColor: isSectionExpanded('quota') ? 'var(--color-surface)' : 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSectionExpanded('quota') ? 'var(--color-surface)' : 'transparent'}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <CreditCard className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.quota.creditsSection}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('quota') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('quota') && (
              <div className="p-4 space-y-4" style={{ borderTop: `1px solid var(--color-border)`, backgroundColor: 'var(--color-surface)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.quota.creditsDescription}
                </p>

                {loading ? (
                  <div className="space-y-3">
                    <div className="h-8 w-full animate-pulse rounded-full" style={{ backgroundColor: 'var(--color-border)' }} />
                  </div>
                ) : quota ? (
                  <div className="space-y-4">
                    {/* Barre : Crédits AI */}
                    <QuotaUsageBar
                      percentage={quota.percentage_used}
                      period={quota.period}
                      label={t.quota.aiCredits ?? 'Crédits AI'}
                      detail={`${creditsUsed.toLocaleString()} ${t.quota.of ?? 'sur'} ${creditsLimit.toLocaleString()}`}
                    />

                    {/* Crédits achetés (persistants) */}
                    {purchasedCreditsBalance > 0 && (
                      <div
                        className="flex items-center justify-between px-3 py-2 rounded-lg text-sm"
                        style={{ backgroundColor: 'var(--color-background, var(--color-surface))' }}
                      >
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {t.quota.purchasedCredits ?? 'Crédits achetés'}
                        </span>
                        <span className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                          {purchasedCreditsBalance.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* Topup button */}
                    {canPurchaseTopup && (
                      <div className="flex justify-center">
                        <AnimatedIconButton
                          icon={Zap}
                          label={t.quota.topupBuyButton ?? 'Recharger des crédits'}
                          onClick={() => setShowTopupModal(true)}
                          variant="default"
                          revealLabelOnHover
                          testId="account-topup-open"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>—</p>
                )}

                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.quota.renewal}
                </p>
              </div>
            )}
          </div>}

          {/* Section: Abonnement — hidden for guests */}
          {!isGuest && <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('subscription')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{ backgroundColor: isSectionExpanded('subscription') ? 'var(--color-surface)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSectionExpanded('subscription') ? 'var(--color-surface)' : 'transparent')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Crown className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.billing.subscription}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('subscription') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('subscription') && (
              <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                <div className="p-3 rounded-lg mt-3" style={{ backgroundColor: 'var(--color-background)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.billing.currentPlan}</span>
                    <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {quota?.plan_display_name ?? '—'}
                    </span>
                  </div>
                </div>
                {user?.email && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.billing.email}</span>
                      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{user.email}</span>
                    </div>
                  </div>
                )}

                {/* Upgrade button for free users */}
                {isFreePlan && (
                  <Button
                    onClick={() => setShowUpgradeModal(true)}
                    size="sm"
                    className="w-full flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                  >
                    <Zap className="w-4 h-4" />
                    {t.billing?.upgradePlan ?? 'Passer à un abonnement payant'}
                  </Button>
                )}

                {/* Switch billing interval */}
                {!isFreePlan && (
                  <div className="space-y-2">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.billing.billingInterval}</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {currentInterval === 'annual' ? t.billing.annual : t.billing.monthly}
                        </span>
                      </div>
                    </div>

                    {/* Auto-renewal / end date */}
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                      {effectiveSubscriptionEndsAt ? (
                        <div className="flex items-center justify-between">
                          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {t.billing.subscriptionEndsAt}
                          </span>
                          <span data-testid="account-subscription-ends-at" className="text-sm font-medium" style={{ color: 'var(--color-warning)' }}>
                            {formatDateForDisplay(effectiveSubscriptionEndsAt)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {t.billing.autoRenewal}
                          </span>
                          <span className="text-sm font-medium" style={{ color: 'var(--color-success)' }}>
                            {t.billing.autoRenewalActive}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-center">
                      <AnimatedIconButton
                        icon={switchingInterval ? Loader2 : RefreshCw}
                        label={currentInterval === 'annual' ? t.billing.switchToMonthly : t.billing.switchToAnnual}
                        onClick={handleSwitchInterval}
                        disabled={switchingInterval}
                        loading={switchingInterval}
                        variant="neutral"
                        revealLabelOnHover
                        testId="account-switch-interval"
                      />
                    </div>
                    {currentInterval === 'monthly' && (
                      <p className="text-[11px] text-center" style={{ color: 'var(--color-text-secondary)' }}>
                        {t.billing.saveWithAnnual}
                      </p>
                    )}
                  </div>
                )}

                {/* Cancel subscription */}
                {!isFreePlan && (
                  <div className="pt-2">
                    {!showCancelDialog ? (
                      <AnimatedIconButton
                        icon={X}
                        label={t.billing.cancelSubscription}
                        onClick={() => setShowCancelDialog(true)}
                        variant="danger"
                        revealLabelOnHover
                        testId="account-cancel-subscription"
                      />
                    ) : (
                      <div className="p-3 rounded-lg space-y-3" style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)' }}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-warning)' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {t.billing.cancelConfirmTitle}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                              {t.billing.cancelConfirmMessage}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowCancelDialog(false)}
                            size="sm"
                            variant="outline"
                            className="flex-1"
                          >
                            {t.billing.toasts ? 'Annuler' : 'Cancel'}
                          </Button>
                          <Button
                            data-testid="account-cancel-confirm"
                            onClick={handleCancelSubscription}
                            disabled={cancelling}
                            size="sm"
                            className="flex-1"
                            style={{ backgroundColor: 'var(--color-warning)', color: '#fff' }}
                          >
                            {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : t.billing.cancelConfirmButton}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>}

          {/* Section: Code promo — hidden for guests */}
          {!isGuest && <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('coupon')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{ backgroundColor: isSectionExpanded('coupon') ? 'var(--color-surface)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSectionExpanded('coupon') ? 'var(--color-surface)' : 'transparent')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Tag className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.billing.promoCode}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('coupon') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('coupon') && (
              <div className="px-4 pb-4" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                <div className="flex gap-2 mt-3">
                  <Input
                    data-testid="account-coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder={t.billing.promoCodePlaceholder}
                    className="flex-1 text-sm"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon(); }}
                  />
                  <AnimatedIconButton
                    icon={applyingCoupon ? Loader2 : Tag}
                    label={t.billing.apply}
                    onClick={handleApplyCoupon}
                    disabled={applyingCoupon || !couponCode.trim()}
                    loading={applyingCoupon}
                    variant="default"
                    revealLabelOnHover
                    ariaLabel={t.account.applyPromoCode}
                    title={t.account.applyPromoCode}
                    testId="account-coupon-apply"
                  />
                </div>
              </div>
            )}
          </div>}

          {/* Section: Parrainage — hidden for free users and guests */}
          {!isGuest && !isFreePlan && (<div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('referral')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{ backgroundColor: isSectionExpanded('referral') ? 'var(--color-surface)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSectionExpanded('referral') ? 'var(--color-surface)' : 'transparent')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Users className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.billing.referralTitle}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('referral') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('referral') && (
              <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                {/* V4-ACCOUNT-04 2026-05-29 — Contexte avantages parrainage */}
                <p className="text-xs mt-3" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  {t.account.referralDescription}
                </p>
                {loadingReferral ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
                  </div>
                ) : referralCode ? (
                  <>
                    <div className="mt-3">
                      <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        {t.billing.referralCode}
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className="flex-1 px-3 py-2 rounded-lg text-sm font-mono font-semibold tracking-wider"
                          style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
                        >
                          {referralCode}
                        </div>
                        <AnimatedIconButton
                          icon={codeCopied ? Check : Copy}
                          label={codeCopied ? t.billing.referralCopied : t.billing.referralCopyButton}
                          onClick={handleCopyReferralCode}
                          variant={codeCopied ? 'success' : 'neutral'}
                          revealLabelOnHover
                        />
                      </div>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {t.billing.referralCount}
                        </span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          {referralCount} {t.billing.referralCountOf} {maxReferrals}
                        </span>
                      </div>
                    </div>

                    {/* Mes récompenses */}
                    <div className="pt-2">
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        {t.billing.myRewards ?? 'Mes récompenses'}
                      </p>
                      {loadingRewards ? (
                        <div className="flex justify-center py-2">
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
                        </div>
                      ) : referralRewards.length === 0 ? (
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {t.billing.noRewards ?? 'Aucune récompense disponible'}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {referralRewards.map((reward) => {
                            const isUsed = reward.applied_count >= reward.max_redemptions;
                            return (
                              <div
                                key={reward.coupon_code}
                                className="flex items-center justify-between gap-2 p-2 rounded-lg"
                                style={{
                                  backgroundColor: 'var(--color-background)',
                                  opacity: isUsed ? 0.5 : 1,
                                }}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                                    {(reward.amount_cents / 100).toFixed(0)}€
                                  </span>
                                  <span className="text-xs font-mono truncate" style={{ color: 'var(--color-text-primary)' }}>
                                    {reward.coupon_code}
                                  </span>
                                  {isUsed && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
                                      {t.billing.rewardUsed ?? 'Utilisé'}
                                    </span>
                                  )}
                                </div>
                                {!isUsed && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <AnimatedIconButton
                                      icon={copiedRewardCode === reward.coupon_code ? Check : Copy}
                                      label={copiedRewardCode === reward.coupon_code ? t.billing.referralCopied : t.billing.referralCopyButton}
                                      onClick={() => handleCopyRewardCode(reward.coupon_code)}
                                      variant={copiedRewardCode === reward.coupon_code ? 'success' : 'neutral'}
                                      size="mini"
                                      revealLabelOnHover
                                    />
                                    <AnimatedIconButton
                                      icon={applyingReward === reward.coupon_code ? Loader2 : Zap}
                                      label={t.billing.applyToRenewal ?? 'Appliquer au renouvellement'}
                                      onClick={() => handleApplyRewardToSubscription(reward.coupon_code)}
                                      variant="default"
                                      size="mini"
                                      revealLabelOnHover
                                      disabled={applyingReward === reward.coupon_code}
                                      testId="account-referral-apply-reward"
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="mt-3">
                    <Button
                      data-testid="account-referral-generate"
                      onClick={handleGenerateReferralCode}
                      disabled={generatingCode}
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                    >
                      {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      {generatingCode ? t.billing.referralGenerating : t.billing.referralNoCode}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>)}

          {/* Section: Factures — hidden for guests */}
          {!isGuest && <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('invoices')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{ backgroundColor: isSectionExpanded('invoices') ? 'var(--color-surface)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSectionExpanded('invoices') ? 'var(--color-surface)' : 'transparent')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <FileText className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.billing.invoices}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('invoices') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {isSectionExpanded('invoices') && (
              <div className="px-4 pb-4" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                {loadingInvoices ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
                  </div>
                ) : invoices.length === 0 ? (
                  <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.billing.noInvoices}
                  </p>
                ) : (
                  <div className="space-y-2 mt-3">
                    {invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        data-testid="account-invoice-row"
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: 'var(--color-background)' }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {invoice.invoice_number || '—'}
                            </span>
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                              style={{
                                backgroundColor: invoice.status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                color: invoice.status === 'paid' ? 'var(--color-success)' : 'var(--color-warning)',
                              }}
                            >
                              {invoice.status}
                            </span>
                          </div>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                            {formatDateForDisplay(invoice.issuing_date || invoice.created_at || '')} · {formatAmount(invoice.amount_cents, invoice.currency)}
                            {invoice.coupon_code_used && (
                              <span className="ml-1" style={{ color: 'var(--color-primary)' }}>
                                ({t.billing.couponUsed}: {invoice.coupon_code_used})
                              </span>
                            )}
                          </p>
                        </div>
                        <Button
                          data-testid="account-invoice-download"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadPdf(invoice)}
                          className="flex-shrink-0 rounded-full"
                          title={t.billing.downloadPdf}
                        >
                          <Download className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>}

          {/* Section: Se déconnecter */}
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid var(--color-border)` }}>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 p-4 transition-colors"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {/* V4-ACCOUNT-05 2026-05-29 — logout neutre, le rouge reste pour "Supprimer mon compte" */}
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                <LogOut className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
              </div>
              <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                {t.sidebar.logout}
              </Label>
            </button>
          </div>

          {/* Section: Supprimer le compte — hidden for guests */}
          {!isGuest && <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-danger)', opacity: 0.9 }}>
            <button
              onClick={() => toggleSection('delete')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{ backgroundColor: showDeleteDialog ? 'var(--color-surface)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSectionExpanded('delete') ? 'var(--color-surface)' : 'transparent')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
                  <Trash2 className="w-5 h-5" style={{ color: 'var(--color-danger)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-danger)' }}>
                  {t.billing.deleteAccount}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isSectionExpanded('delete') ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-danger)' }}
              />
            </button>
            {isSectionExpanded('delete') && (
              <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                <div className="flex items-start gap-2 mt-3">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-danger)' }} />
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.billing.deleteAccountWarning}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.billing.deleteAccountPasswordLabel}
                  </Label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder={t.billing.deleteAccountPasswordPlaceholder}
                    className="text-sm"
                  />
                </div>
                <Button
                  data-testid="account-delete-confirm"
                  onClick={handleDeleteAccount}
                  disabled={deleting || !deletePassword.trim()}
                  size="sm"
                  className="w-full"
                  style={{ backgroundColor: 'var(--color-danger)', color: '#fff' }}
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.billing.deleteAccountConfirmButton}
                </Button>
              </div>
            )}
          </div>}

        </div>
      </div>

      <TopupModal open={showTopupModal} onClose={() => setShowTopupModal(false)} />
      <UpgradePlanModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
}
